import { queryClient } from '@/queries/QueryClientProvider'
import { QUERY_KEYS } from '@/queries/queryKeys'
import {
  notificationsService,
  type GetAllNotificationsResponse,
} from '@/services/api/notifications.service'
import {
  type CreateNotificationProps,
  type NotificationSource,
} from '@/services/types/notifications/mockNotifications'
import { useMutation } from '@tanstack/react-query'

async function editNotificationById<T extends NotificationSource>({
  id,
  data,
}: {
  id: string
  data: Partial<CreateNotificationProps<T>>
}) {
  const response = await notificationsService.editById(id, data)
  return response.data
}

export function useEditNotification<T extends NotificationSource>(notificationSource?: T) {
  return useMutation({
    mutationFn: editNotificationById<T>,
    onMutate: ({ id: updatedNotificationId, data }) => {
      const cachedNotifications = queryClient.getQueryData<GetAllNotificationsResponse<T>>([
        QUERY_KEYS.reviewNotifications.getAll,
        notificationSource,
      ])

      if (!cachedNotifications) return

      const notificationDataBeforeUpdate = cachedNotifications.notifications.find(
        (cachedNotification) => cachedNotification.id === updatedNotificationId,
      )

      queryClient.setQueryData<GetAllNotificationsResponse<T>>(
        [QUERY_KEYS.reviewNotifications.getAll, notificationSource],
        (oldData) => {
          return {
            notifications:
              oldData?.notifications.map((cachedNotification) => {
                if (cachedNotification.id === updatedNotificationId) {
                  return {
                    ...cachedNotification,
                    ...data,
                  }
                }
                return cachedNotification
              }) || [],
            totalNotifications: oldData?.totalNotifications || 0,
          }
        },
      )

      return { notificationDataBeforeUpdate }
    },
    onSuccess: (_data, { id }) =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.reviewNotifications.getAll, { id, notificationSource }],
      }),
    onError: (_error, variables, context) => {
      if (!context) return
      const { notificationDataBeforeUpdate } = context

      if (notificationDataBeforeUpdate) {
        queryClient.setQueryData<GetAllNotificationsResponse<T>>(
          [QUERY_KEYS.reviewNotifications.getAll],
          (oldData) => {
            return {
              notifications:
                oldData?.notifications.map((cachedNotification) => {
                  if (cachedNotification.id === variables.id) {
                    return notificationDataBeforeUpdate
                  }
                  return cachedNotification
                }) || [],
              totalNotifications: oldData?.totalNotifications || 0,
            }
          },
        )
      }
    },
  })
}
